"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";
import { Link } from "@/components/LocaleLink";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Code2, Terminal, Play, CheckCircle2, Sparkles, ArrowRight, Database, Globe, Zap, MessageSquare, Target, Brain } from "lucide-react";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Footer } from "@/components/Footer";
import { useTranslation } from "@/lib/hooks/useTranslation";
import { FaqSection } from "@/components/landing/FaqSection";
import { techFaqFr, techFaqEn } from "./faq-data";
const t = {
  fr: {
    nav: {
      login: "Connexion",
      ctaLong: "Commencer gratuit",
      ctaShort: "S'inscrire"
    },
    demo: {
      tips: ["Bonne utilisation du dictionnaire pour O(1) lookup", "Structure claire et lisible", "Complexité optimale atteinte !"],
      scoreGlobal: "Score Global",
      logic: "Logique",
      syntax: "Syntaxe",
      optimization: "Optimisation"
    },
    hero: {
      badge: "Simulateur d'entretien tech IA",
      titleLine1: "Préparer un entretien tech",
      titleLine2: "FAANG en français, avec l'IA",
      description: <>
          <strong className="text-foreground">Velyq</strong> simule les entretiens
          techniques Google, Meta, Amazon et scale-ups FR (Doctolib, Qonto, Alan) :
          coding en direct, system design et questions data/ML. Python, JavaScript,
          Go, Rust — l&apos;IA analyse ton code ligne par ligne, challenge tes choix
          comme un staff engineer et renvoie un feedback chiffré (logique, syntaxe,
          complexité). Gratuit sans carte bancaire.
        </>,
      ctaPrimary: "Essayer gratuitement",
      ctaSecondary: "Voir les tarifs",
      prepareFor: "Prépare-toi pour :",
      liveDemo: "Démo en direct"
    },
    features: {
      title: "Tout ce qu'il faut pour réussir",
      items: [{
        title: "Coding en temps réel",
        description: "Écris du code pendant que l'IA analyse ta logique et ta syntaxe"
      }, {
        title: "Questions adaptatives",
        description: "L'IA ajuste la difficulté selon tes réponses précédentes"
      }, {
        title: "System Design",
        description: "Conçois des architectures (Twitter, Uber...) avec feedback"
      }, {
        title: "Feedback instantané",
        description: "Comprends tes erreurs et améliore-toi en direct"
      }]
    },
    types: {
      title: "Types d'entretiens couverts",
      subtitle: "De l'algorithme basique au system design avancé, prépare tous les types d'entretiens tech",
      coding: {
        title: "Coding Interview",
        items: ["Algorithmes (LeetCode style)", "Structures de données", "Complexité O(n)", "Clean code"]
      },
      systemDesign: {
        title: "System Design",
        items: ["Architecture distribuée", "Scalabilité", "Base de données", "API Design"]
      },
      data: {
        title: "Data & SQL",
        items: ["Requêtes SQL complexes", "Modélisation données", "ETL & Pipelines", "Analytics"]
      }
    },
    stats: {
      questions: "Questions techniques",
      satisfaction: "Taux de satisfaction",
      available: "Disponible",
      toStart: "Pour commencer"
    },
    resources: {
      badge: "Pour aller plus loin",
      title: "Avant l'entretien tech, passe le filtre ATS.",
      subtitle: "70% des CV tech sont filtrés par un ATS avant même d'arriver au screen RH. Google pondère différemment d'une scale-up FR — voici comment calibrer ton CV et ce qui se joue vraiment côté parsing.",
      card1Tag: "TECH · FINANCE · CONSULTING",
      card1Title: "CV ATS par métier : tech, finance, consulting",
      card1Desc: "Les grilles de lecture ATS réelles pour les 3 secteurs les plus codifiés — FAANG vs scale-ups FR, poids des écoles, mots-clés exacts 2026.",
      card1Cta: "LIRE →",
      card2Tag: "GUIDE COMPLET",
      card2Title: "Passer un ATS en 2026 : ce qui a changé",
      card2Desc: "Parsing sémantique, détection des CV trop IA, skills-based hiring — les 7 règles qui comptent en 2026 et les erreurs de 2022 à oublier.",
      card2Cta: "LIRE →",
      card3Tag: "OUTIL · GRATUIT",
      card3Title: "Analyse ATS de ton CV tech",
      card3Desc: "Teste ton CV contre une offre tech réelle en 30 secondes. Score ATS, keywords manquants, format — sans inscription.",
      card3Cta: "ANALYSER →",
      alsoLabel: "Et aussi :",
      alsoFinance: "Entretien finance",
      alsoConsulting: "Entretien consulting",
      alsoBlog: "Tous les articles"
    },
    cta: {
      title: "Prêt à décrocher ton job en tech ?",
      subtitle: "Rejoins des centaines d'étudiants qui ont boosté leur préparation avec Velyq",
      button: "Commencer gratuitement",
      disclaimer: "Pas de carte bancaire requise • Annule quand tu veux"
    }
  },
  en: {
    nav: {
      login: "Login",
      ctaLong: "Start free",
      ctaShort: "Sign up"
    },
    demo: {
      tips: ["Good use of a dictionary for O(1) lookup", "Clear and readable structure", "Optimal complexity reached!"],
      scoreGlobal: "Overall Score",
      logic: "Logic",
      syntax: "Syntax",
      optimization: "Optimization"
    },
    hero: {
      badge: "AI tech interview simulator",
      titleLine1: "Prepare for a FAANG tech",
      titleLine2: "interview in English, with AI",
      description: <>
          <strong className="text-foreground">Velyq</strong> simulates technical
          interviews at Google, Meta, Amazon and FR scale-ups (Doctolib, Qonto,
          Alan): live coding, system design and data/ML questions. Python,
          JavaScript, Go, Rust — the AI analyzes your code line by line,
          challenges your choices like a staff engineer and returns a quantified
          feedback (logic, syntax, complexity). Free, no credit card.
        </>,
      ctaPrimary: "Try for free",
      ctaSecondary: "View pricing",
      prepareFor: "Prepare for:",
      liveDemo: "Live demo"
    },
    features: {
      title: "Everything you need to succeed",
      items: [{
        title: "Real-time coding",
        description: "Write code while the AI analyzes your logic and syntax"
      }, {
        title: "Adaptive questions",
        description: "The AI adjusts difficulty based on your previous answers"
      }, {
        title: "System Design",
        description: "Design architectures (Twitter, Uber...) with feedback"
      }, {
        title: "Instant feedback",
        description: "Understand your mistakes and improve in real time"
      }]
    },
    types: {
      title: "Interview types covered",
      subtitle: "From basic algorithms to advanced system design, prepare every kind of tech interview",
      coding: {
        title: "Coding Interview",
        items: ["Algorithms (LeetCode style)", "Data structures", "O(n) complexity", "Clean code"]
      },
      systemDesign: {
        title: "System Design",
        items: ["Distributed architecture", "Scalability", "Databases", "API Design"]
      },
      data: {
        title: "Data & SQL",
        items: ["Complex SQL queries", "Data modeling", "ETL & Pipelines", "Analytics"]
      }
    },
    stats: {
      questions: "Technical questions",
      satisfaction: "Satisfaction rate",
      available: "Available",
      toStart: "To get started"
    },
    resources: {
      badge: "Go further",
      title: "Before the tech interview, clear the ATS filter.",
      subtitle: "70% of tech resumes are filtered by an ATS before even reaching the HR screen. Google weights things differently than a FR scale-up — here's how to calibrate your resume and what really matters in parsing.",
      card1Tag: "TECH · FINANCE · CONSULTING",
      card1Title: "ATS resumes by sector: tech, finance, consulting",
      card1Desc: "Real ATS reading grids for the 3 most codified sectors — FAANG vs FR scale-ups, school weighting, exact 2026 keywords.",
      card1Cta: "READ →",
      card2Tag: "FULL GUIDE",
      card2Title: "Passing an ATS in 2026: what changed",
      card2Desc: "Semantic parsing, detection of overly-AI resumes, skills-based hiring — the 7 rules that matter in 2026 and the 2022 mistakes to forget.",
      card2Cta: "READ →",
      card3Tag: "TOOL · FREE",
      card3Title: "ATS analysis of your tech resume",
      card3Desc: "Test your resume against a real tech offer in 30 seconds. ATS score, missing keywords, format — no signup.",
      card3Cta: "ANALYZE →",
      alsoLabel: "Also see:",
      alsoFinance: "Finance interview",
      alsoConsulting: "Consulting interview",
      alsoBlog: "All articles"
    },
    cta: {
      title: "Ready to land your tech job?",
      subtitle: "Join hundreds of students who boosted their prep with Velyq",
      button: "Start for free",
      disclaimer: "No credit card required • Cancel anytime"
    }
  }
};

// Mini IDE Demo Component
function TechInterviewDemo({
  locale
}: {
  locale: "fr" | "en";
}) {
  const content = locale === "fr" ? t.fr : t.en;
  const [currentLine, setCurrentLine] = useState(0);
  const [terminalOutput, setTerminalOutput] = useState<string[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [score, setScore] = useState({
    logic: 0,
    syntax: 0,
    optimization: 0
  });
  const [tip, setTip] = useState<string | null>(null);
  const [loopKey, setLoopKey] = useState(0);
  const codeLines = [{
    text: "def two_sum(nums, target):",
    highlight: false
  }, {
    text: '    """Find two numbers that add up to target"""',
    highlight: false
  }, {
    text: "    seen = {}",
    highlight: true
  }, {
    text: "    for i, num in enumerate(nums):",
    highlight: false
  }, {
    text: "        complement = target - num",
    highlight: true
  }, {
    text: "        if complement in seen:",
    highlight: false
  }, {
    text: "            return [seen[complement], i]",
    highlight: true
  }, {
    text: "        seen[num] = i",
    highlight: false
  }, {
    text: "    return []",
    highlight: false
  }];
  const terminalMessages = ["$ python test_solution.py", "Running test cases...", "✓ Test 1: [2,7,11,15], target=9 → [0,1]", "✓ Test 2: [3,2,4], target=6 → [1,2]", "✓ Test 3: [3,3], target=6 → [0,1]", "", "All tests passed! ✅", "Time complexity: O(n)", "Space complexity: O(n)"];
  const tips = content.demo.tips;
  useEffect(() => {
    // Reset state
    setCurrentLine(0);
    setTerminalOutput([]);
    setIsRunning(false);
    setScore({
      logic: 0,
      syntax: 0,
      optimization: 0
    });
    setTip(null);

    // Animate code highlighting
    const codeInterval = setInterval(() => {
      setCurrentLine(prev => {
        if (prev < codeLines.length - 1) return prev + 1;
        return prev;
      });
    }, 800);

    // Start terminal after code
    const terminalTimeout = setTimeout(() => {
      setIsRunning(true);
      let lineIndex = 0;
      const terminalInterval = setInterval(() => {
        if (lineIndex < terminalMessages.length) {
          setTerminalOutput(prev => [...prev, terminalMessages[lineIndex]]);
          lineIndex++;
        } else {
          clearInterval(terminalInterval);
        }
      }, 400);
    }, 7000);

    // Update scores
    const scoreTimeout = setTimeout(() => {
      setScore({
        logic: 92,
        syntax: 88,
        optimization: 95
      });
    }, 9000);

    // Show tip
    const tipTimeout = setTimeout(() => {
      setTip(tips[Math.floor(Math.random() * tips.length)]);
    }, 10000);

    // Loop
    const loopTimeout = setTimeout(() => {
      setLoopKey(k => k + 1);
    }, 15000);
    return () => {
      clearInterval(codeInterval);
      clearTimeout(terminalTimeout);
      clearTimeout(scoreTimeout);
      clearTimeout(tipTimeout);
      clearTimeout(loopTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loopKey]);
  const overallScore = Math.round((score.logic + score.syntax + score.optimization) / 3);
  return <div className="grid md:grid-cols-2 gap-4">
      {/* IDE Panel */}
      <Card className="bg-[#1e1e1e] border-zinc-700 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2 bg-[#2d2d2d] border-b border-zinc-700">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
          </div>
          <span className="text-xs text-zinc-400 ml-2">solution.py</span>
          <Badge variant="outline" className="ml-auto text-[10px] border-green-500/50 text-green-400">
            Python
          </Badge>
        </div>
        <CardContent className="p-0">
          <div className="font-mono text-sm p-4 space-y-1">
            {codeLines.map((line, i) => <div key={i} className={`flex transition-all duration-300 ${i <= currentLine ? "opacity-100" : "opacity-30"} ${i === currentLine ? "bg-blue-500/20 -mx-4 px-4" : ""}`}>
                <span className="text-zinc-500 w-6 text-right mr-4 select-none">
                  {i + 1}
                </span>
                <span className={line.highlight ? "text-yellow-400" : "text-zinc-300"}>
                  {line.text}
                </span>
              </div>)}
          </div>
        </CardContent>
      </Card>

      {/* Terminal + Scores Panel */}
      <div className="space-y-4">
        {/* Terminal */}
        <Card className="bg-[#0d1117] border-zinc-700">
          <div className="flex items-center gap-2 px-4 py-2 bg-[#161b22] border-b border-zinc-700">
            <Terminal className="w-4 h-4 text-zinc-400" />
            <span className="text-xs text-zinc-400">Terminal</span>
            {isRunning && <Badge className="ml-auto bg-green-500/20 text-green-400 text-[10px]">
                Running
              </Badge>}
          </div>
          <CardContent className="p-4 font-mono text-xs text-green-400 min-h-[180px]">
            {terminalOutput.map((line, i) => <div key={i} className={line?.startsWith("✓") ? "text-green-400" : "text-zinc-400"}>
                {line}
              </div>)}
            {isRunning && terminalOutput.length < terminalMessages.length && <span className="animate-pulse">▋</span>}
          </CardContent>
        </Card>

        {/* Scores */}
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                {content.demo.scoreGlobal}
              </span>
              <span className={`text-2xl font-bold ${overallScore >= 80 ? "text-green-500" : "text-yellow-500"}`}>
                {overallScore || "—"}
              </span>
            </div>
            <Progress value={overallScore} className="h-2" />

            <div className="grid grid-cols-3 gap-2 pt-2 text-xs">
              <div className="text-center">
                <div className="text-muted-foreground">{content.demo.logic}</div>
                <div className="font-bold text-blue-500">{score.logic || "—"}</div>
              </div>
              <div className="text-center">
                <div className="text-muted-foreground">{content.demo.syntax}</div>
                <div className="font-bold text-purple-500">{score.syntax || "—"}</div>
              </div>
              <div className="text-center">
                <div className="text-muted-foreground">{content.demo.optimization}</div>
                <div className="font-bold text-green-500">{score.optimization || "—"}</div>
              </div>
            </div>

            {tip && <div className="mt-3 p-2 bg-green-500/10 rounded-lg border border-green-500/20 animate-in fade-in">
                <div className="flex items-start gap-2 text-xs text-green-600 dark:text-green-400">
                  <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                  {tip}
                </div>
              </div>}
          </CardContent>
        </Card>
      </div>
    </div>;
}
const companies = [{
  name: "Google",
  color: "text-blue-500"
}, {
  name: "Meta",
  color: "text-blue-600"
}, {
  name: "Amazon",
  color: "text-primary"
}, {
  name: "Microsoft",
  color: "text-cyan-500"
}, {
  name: "Apple",
  color: "text-zinc-400"
}, {
  name: "Stripe",
  color: "text-purple-500"
}];
const featureIcons = [Code2, MessageSquare, Brain, Target];
export default function TechInterviewPage() {
  const $ = _c(219);
  const {
    locale
  } = useTranslation();
  const content = locale === "fr" ? t.fr : t.en;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Link href="/"><Logo /></Link>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <LanguageSwitcher />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== content.nav.login) {
    t2 = <Link href="/login" className="hidden sm:block"><Button variant="ghost" size="sm">{content.nav.login}</Button></Link>;
    $[2] = content.nav.login;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== content.nav.ctaLong) {
    t3 = <span className="hidden sm:inline">{content.nav.ctaLong}</span>;
    $[4] = content.nav.ctaLong;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== content.nav.ctaShort) {
    t4 = <span className="sm:hidden">{content.nav.ctaShort}</span>;
    $[6] = content.nav.ctaShort;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <Link href="/register"><Button size="sm">{t3}{t4}</Button></Link>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t2 || $[12] !== t5) {
    t6 = <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50"><div className="container mx-auto px-4 h-16 flex items-center justify-between">{t0}<div className="flex items-center gap-2 sm:gap-4">{t1}{t2}{t5}</div></div></header>;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Sparkles className="w-3 h-3 mr-1" />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== content.hero.badge) {
    t8 = <Badge className="mb-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white border-0">{t7}{content.hero.badge}</Badge>;
    $[15] = content.hero.badge;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <br />;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== content.hero.titleLine1 || $[19] !== content.hero.titleLine2) {
    t10 = <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-500 via-cyan-500 to-green-500 bg-clip-text text-transparent">{content.hero.titleLine1}{t9}{content.hero.titleLine2}</h1>;
    $[18] = content.hero.titleLine1;
    $[19] = content.hero.titleLine2;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== content.hero.description) {
    t11 = <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-8">{content.hero.description}</p>;
    $[21] = content.hero.description;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Play className="w-5 h-5" />;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== content.hero.ctaPrimary) {
    t13 = <Link href="/register"><Button size="lg" className="gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600">{t12}{content.hero.ctaPrimary}</Button></Link>;
    $[24] = content.hero.ctaPrimary;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ArrowRight className="w-4 h-4" />;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== content.hero.ctaSecondary) {
    t15 = <Link href="/pricing"><Button size="lg" variant="outline" className="gap-2">{content.hero.ctaSecondary}{t14}</Button></Link>;
    $[27] = content.hero.ctaSecondary;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t13 || $[30] !== t15) {
    t16 = <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4">{t13}{t15}</div>;
    $[29] = t13;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t16 || $[35] !== t8) {
    t17 = <div className="text-center mb-12">{t8}{t10}{t11}{t16}</div>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t16;
    $[35] = t8;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== content.hero.prepareFor) {
    t18 = <span>{content.hero.prepareFor}</span>;
    $[37] = content.hero.prepareFor;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = companies.map(_temp);
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t18) {
    t20 = <div className="flex flex-wrap justify-center items-center gap-6 mb-16 text-sm text-muted-foreground">{t18}{t19}</div>;
    $[40] = t18;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Zap className="w-3 h-3" />;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== content.hero.liveDemo) {
    t22 = <div className="text-center mb-6"><Badge variant="outline" className="gap-1">{t21}{content.hero.liveDemo}</Badge></div>;
    $[43] = content.hero.liveDemo;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== locale) {
    t23 = <TechInterviewDemo locale={locale} />;
    $[45] = locale;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t22 || $[48] !== t23) {
    t24 = <div className="mb-8">{t22}{t23}</div>;
    $[47] = t22;
    $[48] = t23;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t17 || $[51] !== t20 || $[52] !== t24) {
    t25 = <section className="py-20 px-4"><div className="container mx-auto max-w-6xl">{t17}{t20}{t24}</div></section>;
    $[50] = t17;
    $[51] = t20;
    $[52] = t24;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== content.features.title) {
    t26 = <h2 className="text-3xl font-bold text-center mb-12">{content.features.title}</h2>;
    $[54] = content.features.title;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== content.features.items) {
    t27 = content.features.items.map(_temp2);
    $[56] = content.features.items;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== t27) {
    t28 = <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">{t27}</div>;
    $[58] = t27;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== t26 || $[61] !== t28) {
    t29 = <section className="py-20 px-4 bg-muted/30"><div className="container mx-auto max-w-6xl">{t26}{t28}</div></section>;
    $[60] = t26;
    $[61] = t28;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== content.types.title) {
    t30 = <h2 className="text-3xl font-bold text-center mb-4">{content.types.title}</h2>;
    $[63] = content.types.title;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== content.types.subtitle) {
    t31 = <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">{content.types.subtitle}</p>;
    $[65] = content.types.subtitle;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Code2 className="w-8 h-8 text-blue-500 mb-4" />;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  let t33;
  if ($[68] !== content.types.coding.title) {
    t33 = <h3 className="font-semibold mb-2">{content.types.coding.title}</h3>;
    $[68] = content.types.coding.title;
    $[69] = t33;
  } else {
    t33 = $[69];
  }
  let t34;
  if ($[70] !== content.types.coding.items) {
    t34 = content.types.coding.items.map(_temp3);
    $[70] = content.types.coding.items;
    $[71] = t34;
  } else {
    t34 = $[71];
  }
  let t35;
  if ($[72] !== t34) {
    t35 = <ul className="text-sm text-muted-foreground space-y-1">{t34}</ul>;
    $[72] = t34;
    $[73] = t35;
  } else {
    t35 = $[73];
  }
  let t36;
  if ($[74] !== t33 || $[75] !== t35) {
    t36 = <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20"><CardContent className="pt-6">{t32}{t33}{t35}</CardContent></Card>;
    $[74] = t33;
    $[75] = t35;
    $[76] = t36;
  } else {
    t36 = $[76];
  }
  let t37;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <Globe className="w-8 h-8 text-purple-500 mb-4" />;
    $[77] = t37;
  } else {
    t37 = $[77];
  }
  let t38;
  if ($[78] !== content.types.systemDesign.title) {
    t38 = <h3 className="font-semibold mb-2">{content.types.systemDesign.title}</h3>;
    $[78] = content.types.systemDesign.title;
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  let t39;
  if ($[80] !== content.types.systemDesign.items) {
    t39 = content.types.systemDesign.items.map(_temp4);
    $[80] = content.types.systemDesign.items;
    $[81] = t39;
  } else {
    t39 = $[81];
  }
  let t40;
  if ($[82] !== t39) {
    t40 = <ul className="text-sm text-muted-foreground space-y-1">{t39}</ul>;
    $[82] = t39;
    $[83] = t40;
  } else {
    t40 = $[83];
  }
  let t41;
  if ($[84] !== t38 || $[85] !== t40) {
    t41 = <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/20"><CardContent className="pt-6">{t37}{t38}{t40}</CardContent></Card>;
    $[84] = t38;
    $[85] = t40;
    $[86] = t41;
  } else {
    t41 = $[86];
  }
  let t42;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <Database className="w-8 h-8 text-green-500 mb-4" />;
    $[87] = t42;
  } else {
    t42 = $[87];
  }
  let t43;
  if ($[88] !== content.types.data.title) {
    t43 = <h3 className="font-semibold mb-2">{content.types.data.title}</h3>;
    $[88] = content.types.data.title;
    $[89] = t43;
  } else {
    t43 = $[89];
  }
  let t44;
  if ($[90] !== content.types.data.items) {
    t44 = content.types.data.items.map(_temp5);
    $[90] = content.types.data.items;
    $[91] = t44;
  } else {
    t44 = $[91];
  }
  let t45;
  if ($[92] !== t44) {
    t45 = <ul className="text-sm text-muted-foreground space-y-1">{t44}</ul>;
    $[92] = t44;
    $[93] = t45;
  } else {
    t45 = $[93];
  }
  let t46;
  if ($[94] !== t43 || $[95] !== t45) {
    t46 = <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20"><CardContent className="pt-6">{t42}{t43}{t45}</CardContent></Card>;
    $[94] = t43;
    $[95] = t45;
    $[96] = t46;
  } else {
    t46 = $[96];
  }
  let t47;
  if ($[97] !== t36 || $[98] !== t41 || $[99] !== t46) {
    t47 = <div className="grid md:grid-cols-3 gap-6">{t36}{t41}{t46}</div>;
    $[97] = t36;
    $[98] = t41;
    $[99] = t46;
    $[100] = t47;
  } else {
    t47 = $[100];
  }
  let t48;
  if ($[101] !== t30 || $[102] !== t31 || $[103] !== t47) {
    t48 = <section className="py-20 px-4"><div className="container mx-auto max-w-6xl">{t30}{t31}{t47}</div></section>;
    $[101] = t30;
    $[102] = t31;
    $[103] = t47;
    $[104] = t48;
  } else {
    t48 = $[104];
  }
  let t49;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <div className="text-4xl font-bold text-primary mb-2">500+</div>;
    $[105] = t49;
  } else {
    t49 = $[105];
  }
  let t50;
  if ($[106] !== content.stats.questions) {
    t50 = <div>{t49}<div className="text-sm text-muted-foreground">{content.stats.questions}</div></div>;
    $[106] = content.stats.questions;
    $[107] = t50;
  } else {
    t50 = $[107];
  }
  let t51;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <div className="text-4xl font-bold text-primary mb-2">92%</div>;
    $[108] = t51;
  } else {
    t51 = $[108];
  }
  let t52;
  if ($[109] !== content.stats.satisfaction) {
    t52 = <div>{t51}<div className="text-sm text-muted-foreground">{content.stats.satisfaction}</div></div>;
    $[109] = content.stats.satisfaction;
    $[110] = t52;
  } else {
    t52 = $[110];
  }
  let t53;
  if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <div className="text-4xl font-bold text-primary mb-2">24/7</div>;
    $[111] = t53;
  } else {
    t53 = $[111];
  }
  let t54;
  if ($[112] !== content.stats.available) {
    t54 = <div>{t53}<div className="text-sm text-muted-foreground">{content.stats.available}</div></div>;
    $[112] = content.stats.available;
    $[113] = t54;
  } else {
    t54 = $[113];
  }
  let t55;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <div className="text-4xl font-bold text-primary mb-2">5min</div>;
    $[114] = t55;
  } else {
    t55 = $[114];
  }
  let t56;
  if ($[115] !== content.stats.toStart) {
    t56 = <div>{t55}<div className="text-sm text-muted-foreground">{content.stats.toStart}</div></div>;
    $[115] = content.stats.toStart;
    $[116] = t56;
  } else {
    t56 = $[116];
  }
  let t57;
  if ($[117] !== t50 || $[118] !== t52 || $[119] !== t54 || $[120] !== t56) {
    t57 = <section className="py-20 px-4 bg-muted/30"><div className="container mx-auto max-w-4xl"><div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">{t50}{t52}{t54}{t56}</div></div></section>;
    $[117] = t50;
    $[118] = t52;
    $[119] = t54;
    $[120] = t56;
    $[121] = t57;
  } else {
    t57 = $[121];
  }
  let t58;
  if ($[122] !== content.resources.badge) {
    t58 = <Badge variant="outline" className="mb-4">{content.resources.badge}</Badge>;
    $[122] = content.resources.badge;
    $[123] = t58;
  } else {
    t58 = $[123];
  }
  let t59;
  if ($[124] !== content.resources.title) {
    t59 = <h2 className="text-3xl font-bold mb-4">{content.resources.title}</h2>;
    $[124] = content.resources.title;
    $[125] = t59;
  } else {
    t59 = $[125];
  }
  let t60;
  if ($[126] !== content.resources.subtitle) {
    t60 = <p className="text-muted-foreground max-w-2xl mx-auto">{content.resources.subtitle}</p>;
    $[126] = content.resources.subtitle;
    $[127] = t60;
  } else {
    t60 = $[127];
  }
  let t61;
  if ($[128] !== t58 || $[129] !== t59 || $[130] !== t60) {
    t61 = <div className="text-center mb-12">{t58}{t59}{t60}</div>;
    $[128] = t58;
    $[129] = t59;
    $[130] = t60;
    $[131] = t61;
  } else {
    t61 = $[131];
  }
  let t62;
  if ($[132] !== content.resources.card1Tag) {
    t62 = <div className="text-xs font-mono text-primary mb-3">{content.resources.card1Tag}</div>;
    $[132] = content.resources.card1Tag;
    $[133] = t62;
  } else {
    t62 = $[133];
  }
  let t63;
  if ($[134] !== content.resources.card1Title) {
    t63 = <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">{content.resources.card1Title}</h3>;
    $[134] = content.resources.card1Title;
    $[135] = t63;
  } else {
    t63 = $[135];
  }
  let t64;
  if ($[136] !== content.resources.card1Desc) {
    t64 = <p className="text-sm text-muted-foreground mb-4">{content.resources.card1Desc}</p>;
    $[136] = content.resources.card1Desc;
    $[137] = t64;
  } else {
    t64 = $[137];
  }
  let t65;
  if ($[138] !== content.resources.card1Cta) {
    t65 = <span className="text-xs font-mono text-primary">{content.resources.card1Cta}</span>;
    $[138] = content.resources.card1Cta;
    $[139] = t65;
  } else {
    t65 = $[139];
  }
  let t66;
  if ($[140] !== t62 || $[141] !== t63 || $[142] !== t64 || $[143] !== t65) {
    t66 = <Link href="/blog/cv-ats-tech-finance-consulting" className="group"><Card className="h-full hover:border-primary/50 transition-colors"><CardContent className="pt-6">{t62}{t63}{t64}{t65}</CardContent></Card></Link>;
    $[140] = t62;
    $[141] = t63;
    $[142] = t64;
    $[143] = t65;
    $[144] = t66;
  } else {
    t66 = $[144];
  }
  let t67;
  if ($[145] !== content.resources.card2Tag) {
    t67 = <div className="text-xs font-mono text-primary mb-3">{content.resources.card2Tag}</div>;
    $[145] = content.resources.card2Tag;
    $[146] = t67;
  } else {
    t67 = $[146];
  }
  let t68;
  if ($[147] !== content.resources.card2Title) {
    t68 = <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">{content.resources.card2Title}</h3>;
    $[147] = content.resources.card2Title;
    $[148] = t68;
  } else {
    t68 = $[148];
  }
  let t69;
  if ($[149] !== content.resources.card2Desc) {
    t69 = <p className="text-sm text-muted-foreground mb-4">{content.resources.card2Desc}</p>;
    $[149] = content.resources.card2Desc;
    $[150] = t69;
  } else {
    t69 = $[150];
  }
  let t70;
  if ($[151] !== content.resources.card2Cta) {
    t70 = <span className="text-xs font-mono text-primary">{content.resources.card2Cta}</span>;
    $[151] = content.resources.card2Cta;
    $[152] = t70;
  } else {
    t70 = $[152];
  }
  let t71;
  if ($[153] !== t67 || $[154] !== t68 || $[155] !== t69 || $[156] !== t70) {
    t71 = <Link href="/blog/comment-passer-ats-cv-2026" className="group"><Card className="h-full hover:border-primary/50 transition-colors"><CardContent className="pt-6">{t67}{t68}{t69}{t70}</CardContent></Card></Link>;
    $[153] = t67;
    $[154] = t68;
    $[155] = t69;
    $[156] = t70;
    $[157] = t71;
  } else {
    t71 = $[157];
  }
  let t72;
  if ($[158] !== content.resources.card3Tag) {
    t72 = <div className="text-xs font-mono text-primary mb-3">{content.resources.card3Tag}</div>;
    $[158] = content.resources.card3Tag;
    $[159] = t72;
  } else {
    t72 = $[159];
  }
  let t73;
  if ($[160] !== content.resources.card3Title) {
    t73 = <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">{content.resources.card3Title}</h3>;
    $[160] = content.resources.card3Title;
    $[161] = t73;
  } else {
    t73 = $[161];
  }
  let t74;
  if ($[162] !== content.resources.card3Desc) {
    t74 = <p className="text-sm text-muted-foreground mb-4">{content.resources.card3Desc}</p>;
    $[162] = content.resources.card3Desc;
    $[163] = t74;
  } else {
    t74 = $[163];
  }
  let t75;
  if ($[164] !== content.resources.card3Cta) {
    t75 = <span className="text-xs font-mono text-primary">{content.resources.card3Cta}</span>;
    $[164] = content.resources.card3Cta;
    $[165] = t75;
  } else {
    t75 = $[165];
  }
  let t76;
  if ($[166] !== t72 || $[167] !== t73 || $[168] !== t74 || $[169] !== t75) {
    t76 = <Link href="/analyse-cv" className="group"><Card className="h-full hover:border-primary/50 transition-colors"><CardContent className="pt-6">{t72}{t73}{t74}{t75}</CardContent></Card></Link>;
    $[166] = t72;
    $[167] = t73;
    $[168] = t74;
    $[169] = t75;
    $[170] = t76;
  } else {
    t76 = $[170];
  }
  let t77;
  if ($[171] !== t66 || $[172] !== t71 || $[173] !== t76) {
    t77 = <div className="grid md:grid-cols-3 gap-6 mb-10">{t66}{t71}{t76}</div>;
    $[171] = t66;
    $[172] = t71;
    $[173] = t76;
    $[174] = t77;
  } else {
    t77 = $[174];
  }
  let t78;
  if ($[175] !== content.resources.alsoLabel) {
    t78 = <span className="text-xs text-muted-foreground font-mono self-center mr-2">{content.resources.alsoLabel}</span>;
    $[175] = content.resources.alsoLabel;
    $[176] = t78;
  } else {
    t78 = $[176];
  }
  let t79;
  if ($[177] !== content.resources.alsoFinance) {
    t79 = <Link href="/entretien-finance"><Badge variant="outline" className="hover:border-primary cursor-pointer">{content.resources.alsoFinance}</Badge></Link>;
    $[177] = content.resources.alsoFinance;
    $[178] = t79;
  } else {
    t79 = $[178];
  }
  let t80;
  if ($[179] !== content.resources.alsoConsulting) {
    t80 = <Link href="/entretien-consulting"><Badge variant="outline" className="hover:border-primary cursor-pointer">{content.resources.alsoConsulting}</Badge></Link>;
    $[179] = content.resources.alsoConsulting;
    $[180] = t80;
  } else {
    t80 = $[180];
  }
  let t81;
  if ($[181] !== content.resources.alsoBlog) {
    t81 = <Link href="/blog"><Badge variant="outline" className="hover:border-primary cursor-pointer">{content.resources.alsoBlog}</Badge></Link>;
    $[181] = content.resources.alsoBlog;
    $[182] = t81;
  } else {
    t81 = $[182];
  }
  let t82;
  if ($[183] !== t78 || $[184] !== t79 || $[185] !== t80 || $[186] !== t81) {
    t82 = <div className="flex flex-wrap gap-3 justify-center pt-6 border-t border-border">{t78}{t79}{t80}{t81}</div>;
    $[183] = t78;
    $[184] = t79;
    $[185] = t80;
    $[186] = t81;
    $[187] = t82;
  } else {
    t82 = $[187];
  }
  let t83;
  if ($[188] !== t61 || $[189] !== t77 || $[190] !== t82) {
    t83 = <section className="py-20 px-4"><div className="container mx-auto max-w-5xl">{t61}{t77}{t82}</div></section>;
    $[188] = t61;
    $[189] = t77;
    $[190] = t82;
    $[191] = t83;
  } else {
    t83 = $[191];
  }
  const t84 = locale === "fr" ? techFaqFr : techFaqEn;
  const t85 = locale === "fr" ? "Questions fr\xE9quentes" : "Frequently asked questions";
  let t86;
  if ($[192] !== t84 || $[193] !== t85) {
    t86 = <FaqSection items={t84} title={t85} />;
    $[192] = t84;
    $[193] = t85;
    $[194] = t86;
  } else {
    t86 = $[194];
  }
  let t87;
  if ($[195] !== content.cta.title) {
    t87 = <h2 className="text-3xl font-bold mb-4">{content.cta.title}</h2>;
    $[195] = content.cta.title;
    $[196] = t87;
  } else {
    t87 = $[196];
  }
  let t88;
  if ($[197] !== content.cta.subtitle) {
    t88 = <p className="text-muted-foreground mb-8 max-w-xl mx-auto">{content.cta.subtitle}</p>;
    $[197] = content.cta.subtitle;
    $[198] = t88;
  } else {
    t88 = $[198];
  }
  let t89;
  if ($[199] === Symbol.for("react.memo_cache_sentinel")) {
    t89 = <ArrowRight className="w-4 h-4" />;
    $[199] = t89;
  } else {
    t89 = $[199];
  }
  let t90;
  if ($[200] !== content.cta.button) {
    t90 = <Link href="/register"><Button size="lg" className="gap-2 bg-gradient-to-r from-blue-500 to-cyan-500">{content.cta.button}{t89}</Button></Link>;
    $[200] = content.cta.button;
    $[201] = t90;
  } else {
    t90 = $[201];
  }
  let t91;
  if ($[202] !== content.cta.disclaimer) {
    t91 = <p className="text-xs text-muted-foreground mt-4">{content.cta.disclaimer}</p>;
    $[202] = content.cta.disclaimer;
    $[203] = t91;
  } else {
    t91 = $[203];
  }
  let t92;
  if ($[204] !== t87 || $[205] !== t88 || $[206] !== t90 || $[207] !== t91) {
    t92 = <section className="py-20 px-4"><div className="container mx-auto max-w-4xl text-center">{t87}{t88}{t90}{t91}</div></section>;
    $[204] = t87;
    $[205] = t88;
    $[206] = t90;
    $[207] = t91;
    $[208] = t92;
  } else {
    t92 = $[208];
  }
  let t93;
  if ($[209] === Symbol.for("react.memo_cache_sentinel")) {
    t93 = <Footer />;
    $[209] = t93;
  } else {
    t93 = $[209];
  }
  let t94;
  if ($[210] !== t25 || $[211] !== t29 || $[212] !== t48 || $[213] !== t57 || $[214] !== t6 || $[215] !== t83 || $[216] !== t86 || $[217] !== t92) {
    t94 = <div className="min-h-screen bg-background">{t6}{t25}{t29}{t48}{t57}{t83}{t86}{t92}{t93}</div>;
    $[210] = t25;
    $[211] = t29;
    $[212] = t48;
    $[213] = t57;
    $[214] = t6;
    $[215] = t83;
    $[216] = t86;
    $[217] = t92;
    $[218] = t94;
  } else {
    t94 = $[218];
  }
  return t94;
}
function _temp5(item_1, i_2) {
  return <li key={i_2}>• {item_1}</li>;
}
function _temp4(item_0, i_1) {
  return <li key={i_1}>• {item_0}</li>;
}
function _temp3(item, i_0) {
  return <li key={i_0}>• {item}</li>;
}
function _temp2(feature, i) {
  const Icon = featureIcons[i];
  return <Card key={i} className="border-2 hover:border-primary/50 transition-colors"><CardContent className="pt-6"><Icon className="w-10 h-10 text-primary mb-4" /><h3 className="font-semibold mb-2">{feature.title}</h3><p className="text-sm text-muted-foreground">{feature.description}</p></CardContent></Card>;
}
function _temp(company) {
  return <span key={company.name} className={`font-semibold ${company.color}`}>{company.name}</span>;
}